import { useEffect, useState } from "react";

import AppButton from "../common/AppButton";
import AppForm from "../common/AppForm";
import AppLoader from "../common/AppLoader";
import AppModal from "../common/AppModal";
import AppSwitchButton from "../common/AppSwitchButton";
import AppTable from "../common/AppTable";
import CSVModal from "../common/CsvFormat";
import AddNewCompetitorCSVModal from "../common/CsvFormat";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import DeleteIcon from "@mui/icons-material/Delete";
import { styled } from "@mui/material/styles";
import DocumentIcon from "@mui/icons-material/DocumentScanner";
import DocumentOutlinedIcon from "@mui/icons-material/DocumentScannerOutlined";

import { useAsync, usePagination } from "../../hooks";
import {
  getWebsites,
  updateWebsites,
  enableAllWebsites,
  enableScraping,
  enableScrapingById,
  postWebsiteFile,
  exportWebsiteFile,
  postWebsite,
  deleteWebsite,
  downloadNewCompetitorCSVTemplate,
  importCompetitorUrlsFromCSV,
  fetchErrorLog,
} from "../../services";
import { getScrapingProgress } from "../../services/scrapingProgress";
import { CreateWebsiteData, GetWebsite, WebsiteData } from "../../interface";
import { useUser } from "../../context/UserContext";
import { websiteForm } from "../../constants";
import { websiteSchema } from "../../utils";
import { ErrorCountModal } from "../ErrorCountModal/ErrorCountModal";
import { ScrapperError } from "../ErrorCountModal/ErrorCountModal";

const IconWrapper = styled("span")(() => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "space-between",
  position: "relative",
  width: 28,
  height: 28,
  margin: "0 6px",
  "& .default-icon": { display: "block" },
  "& .hover-icon": {
    display: "none",
    position: "absolute",
    top: 0,
    left: 0,
  },
  "&:hover .default-icon": { display: "none" },
  "&:hover .hover-icon": { display: "block" },
}));

interface WebsitesTableProps {
  onScrapingStart?: () => void;
}

const WebsitesTable: React.FC<WebsitesTableProps> = ({ onScrapingStart }) => {
  const { user } = useUser();

  const [websites, setWebsites] = useState<WebsiteData[]>([]);
  const [loadingOverlay, setLoadingOverlay] = useState(false);
  const [csvModalOpen, setCsvModalOpen] = useState(false);
  const [visibility, setVisibility] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [modalMode, setModalMode] = useState<"add" | "edit" | "delete" | null>(
    null,
  );
  const [selectedWebsite, setSelectedWebsite] = useState<WebsiteData | null>(
    null,
  );
  const [deleteWebsiteId, setDeleteWebsiteId] = useState<number | null>(null);
  const [scrapingInProgress, setScrapingInProgress] = useState(false);
  const [scrapingProgress, setScrapingProgress] = useState(0);
  const [scrapingMessage, setScrapingMessage] = useState("");
  const [errorModal, setErrorModal] = useState<boolean>(false);
  const [errorLogs, setErrorLogs] = useState(null);

  const globalEnable =
    websites.length > 0 && websites.every((w) => w.isEnabled);

  const { meta, setMeta, handleChangePage, handleChangeRowsPerPage } =
    usePagination(5, "websitesTable");

  const { loading, error, execute } = useAsync<GetWebsite, [number, number]>();
  const {
    loading: updateLoading,
    error: updateError,
    execute: updateExecute,
  } = useAsync<string, [number, Partial<WebsiteData>]>();
  const {
    loading: loadingPost,
    execute: executePost,
    error: errorPost,
  } = useAsync<string, [CreateWebsiteData]>();
  const {
    loading: updateAllLoading,
    error: updateAllError,
    execute: updateAllExecute,
  } = useAsync<string, [Partial<WebsiteData>]>();
  const {
    loading: loadingPatchWebsite,
    execute: executePatchWebsite,
    error: errorPatchWebsite,
  } = useAsync<string, [number, Partial<WebsiteData>]>();
  const {
    loading: loadingDelete,
    execute: executeDelete,
    error: errorDelete,
  } = useAsync<string, [number]>();
  const { execute: enableScrapingExecute } = useAsync<string, []>();
  const { execute: enableOneScrapingExecute } = useAsync<string, [number]>();

  useEffect(() => {
    execute(getWebsites, meta.currentPage, meta.pageSize).then((response) => {
      setWebsites(response.data);
      setMeta(response.meta);
    });
  }, [meta.currentPage, meta.pageSize, execute, setMeta]);

  // Poll for scraping status
  useEffect(() => {
    const checkScrapingStatus = async () => {
      try {
        const progressData = await getScrapingProgress();
        if (progressData.overallStatus === "in_progress") {
          setScrapingInProgress(true);
          setScrapingProgress(progressData.overallProgress);
          setScrapingMessage(
            `(${progressData.processedUrls ?? 0}/${progressData.totalUrls ?? 0} URLs)`,
          );
        } else {
          setScrapingInProgress(false);
        }
      } catch (error) {
        // Silently fail - user is not on progress page
        setScrapingInProgress(false);
      }
    };

    // Check immediately
    checkScrapingStatus();

    // Poll every 5 seconds
    const interval = setInterval(checkScrapingStatus, 5000);

    return () => clearInterval(interval);
  }, []);

  const handleCloseModal = () => {
    setModalMode(null);
    setSelectedWebsite(null);
    setDeleteWebsiteId(null);
    setOpenModal(false);
  };

  const handleOpenAddModal = () => {
    setModalMode("add");
    setSelectedWebsite(null);
    setOpenModal(true);
  };

  const handleOpenEditModal = (website: WebsiteData) => {
    setModalMode("edit");
    setSelectedWebsite(website);
    setOpenModal(true);
  };

  const handleNewCompetitorCSVModal = (website: WebsiteData) => {
    setVisibility(true);
    setSelectedWebsite(website);
  };

  const handleOpenDeleteModal = (id: number) => {
    setModalMode("delete");
    setDeleteWebsiteId(id);
    setOpenModal(true);
  };

  const handleSubmitWebsite = async (values: CreateWebsiteData) => {
    try {
      if (modalMode === "edit" && selectedWebsite) {
        handleCloseModal();
        const message = await executePatchWebsite(
          updateWebsites,
          selectedWebsite.id!,
          values,
        );
        console.log("✅ Website updated:", message);

        setWebsites((prev) =>
          prev.map((site) =>
            site.id === selectedWebsite.id ? { ...site, ...values } : site,
          ),
        );
      } else if (modalMode === "add") {
        handleCloseModal();
        const message = await executePost(postWebsite, values);
        console.log("✅ Website added:", message);

        const response = await execute(
          getWebsites,
          meta.currentPage,
          meta.pageSize,
        );
        setWebsites(response.data);
        setMeta(response.meta);
      }
    } catch (err) {
      console.error("❌ Failed to save website", err);
    }
  };

  const handleErrorCountClick = async (id: number) => {
    try {
      const response = await fetchErrorLog(id);
      //@ts-ignore
      setErrorLogs(response.data);
      setErrorModal(true);
    } catch (error) {
      console.log("error fetching error logs from db");
    }
  };

  const handleDelete = async () => {
    if (!deleteWebsiteId) return;

    handleCloseModal();

    try {
      const message = await executeDelete(deleteWebsite, deleteWebsiteId);
      console.log("✅ Deleted:", message);
      setWebsites((prev) => prev.filter((user) => user.id !== deleteWebsiteId));
      setMeta((prev) => {
        const newTotalRecords = prev.totalRecords - 1;
        const newTotalPages = Math.ceil(newTotalRecords / prev.pageSize);
        const newCurrentPage = Math.min(prev.currentPage, newTotalPages || 1);
        return {
          ...prev,
          totalRecords: newTotalRecords,
          totalPages: newTotalPages,
          currentPage: newCurrentPage,
        };
      });
      setDeleteWebsiteId(null);
    } catch (err) {
      console.error("❌ Delete error:", err);
    }
  };

  const handleSwitchButton = async (id: number, checked: boolean) => {
    try {
      setWebsites((prev) =>
        prev.map((site) =>
          site.id === id ? { ...site, isEnabled: checked } : site,
        ),
      );
      await updateExecute(updateWebsites, id, { isEnabled: checked });
    } catch (err) {
      console.error(err);
      setWebsites((prev) =>
        prev.map((site) =>
          site.id === id ? { ...site, isEnabled: !checked } : site,
        ),
      );
    }
  };

  const handleAllSwitchButton = async (checked: boolean) => {
    try {
      setWebsites((prev) =>
        prev.map((site) => ({ ...site, isEnabled: checked })),
      );
      await updateAllExecute(enableAllWebsites, { isEnabled: checked });
    } catch (err) {
      console.error(err);
      setWebsites((prev) =>
        prev.map((site) => ({ ...site, isEnabled: !checked })),
      );
    }
  };

  const handleEnableOneScraping = (id: number) => {
    alert("Scraping started for this website...");
    const now = new Date().toISOString();
    setWebsites((prev) =>
      prev.map((site) => (site.id === id ? { ...site, scrapedAt: now } : site)),
    );

    if (onScrapingStart) {
      onScrapingStart();
    }

    enableOneScrapingExecute(enableScrapingById, id)
      .then(() => {
        // User will see progress on the progress page
      })
      .catch((err) => {
        console.error(err);
        alert("Scraping failed for this website.");
      });
  };

  const handleEnableScraping = () => {
    alert("Scraping started for all enabled websites...");

    const now = new Date().toISOString();
    setWebsites((prev) =>
      prev.map((site) => (site.isEnabled ? { ...site, scrapedAt: now } : site)),
    );

    if (onScrapingStart) {
      onScrapingStart();
    }

    enableScrapingExecute(enableScraping)
      .then(() => {
        // User will see progress on the progress page
      })
      .catch((err) => {
        console.error(err);
        alert("Scraping failed for some websites.");
      });
  };

  const handleImportCSV = async (file: File) => {
    const formData = new FormData();
    formData.append("file", file);

    try {
      setLoadingOverlay(true);
      await postWebsiteFile(formData);
      execute(getWebsites, meta.currentPage, meta.pageSize).then((response) => {
        setWebsites(response.data);
        setMeta(response.meta);
      });
    } catch (err: any) {
      console.error(err);
      alert(`${err.message}`);
    } finally {
      setLoadingOverlay(false);
    }
  };

  const handleCompetitorCSVUpload = async (id: number, file: File) => {
    try {
      await importCompetitorUrlsFromCSV(id, file);
    } catch (error: any) {
      console.error("error while upload competitor csv", error);
    }
  };

  const handleCompetitorCSVDownload = async (id: number) => {
    try {
      await downloadNewCompetitorCSVTemplate(id);
    } catch (err: any) {
      console.error(err);
    }
  };

  const extendedColumns = [
    { id: "id", label: "id" },
    { id: "name", label: "Name" },
    {
      id: "url",
      label: "URL",
      render: (site: WebsiteData) => (
        <a
          href={site.url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-dark text-decoration-none"
          onClick={(e) => e.stopPropagation()}
        >
          {site.url}
        </a>
      ),
    },
    {
      id: "lastRun",
      label: "Last Run",
      render: (site: WebsiteData) =>
        site.scrapedAt
          ? new Date(site.scrapedAt).toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
              second: "2-digit",
              hour12: false,
            })
          : "N/A",
    },
    {
      id: "duration",
      label: "Duration",
      render: (site: WebsiteData) => site.timeDuration || "N/A",
    },
    {
      id: "successRate",
      label: "Success Rate",
      render: (site: WebsiteData) =>
        site.successRate !== undefined ? `${site.successRate}%` : "N/A",
    },
    {
      id: "itemsScraped",
      label: "Items Scraped",
      render: (site: WebsiteData) =>
        site.itemsScraped !== undefined ? site.itemsScraped : "N/A",
    },
    {
      id: "isSourceSite",
      label: "Site Type",
      render: (site: WebsiteData) => (
        <span
          style={{
            display: "inline-block",
            padding: "4px 12px",
            borderRadius: "12px",
            fontSize: "0.875rem",
            fontWeight: "500",
            backgroundColor: site.isSourceSite ? "#d1e7dd" : "#e9ecef",
            color: site.isSourceSite ? "#0f5132" : "#495057",
            border: site.isSourceSite
              ? "1px solid #badbcc"
              : "1px solid #dee2e6",
          }}
        >
          {site.isSourceSite ? "Site" : "Competitor Site"}
        </span>
      ),
    },
    {
      id: "errorCount",
      label: "Error Count",
      render: (site: WebsiteData) =>
        site.errorCount !== undefined ? site.errorCount : "N/A",
    },
    {
      id: "enabled",
      label: "Enable",
      render: (site: WebsiteData) => (
        <AppSwitchButton
          checked={site.isEnabled}
          onChange={(e) => handleSwitchButton(site.id, e.target.checked)}
        />
      ),
    },
    {
      id: "runNow",
      label: "Scrape",
      render: (site: WebsiteData) => (
        <AppButton
          label="Run Now"
          size="sm"
          fullWidth={false}
          onClick={() => handleEnableOneScraping(site.id)}
        />
      ),
    },
    {
      id: "actions",
      label: "Actions",
      render: (website: WebsiteData) => (
        <>
          <IconWrapper>
            <EditOutlinedIcon
              className="default-icon"
              sx={{ color: "#1976d2", cursor: "pointer" }}
            />
            <EditIcon
              className="hover-icon"
              onClick={() => handleOpenEditModal(website)}
              sx={{ color: "#1976d2", cursor: "pointer" }}
            />
          </IconWrapper>
          <IconWrapper>
            <DocumentOutlinedIcon
              className="default-icon"
              sx={{ color: "#19d27f", cursor: "pointer" }}
            />
            <DocumentIcon
              className="hover-icon"
              onClick={() => handleNewCompetitorCSVModal(website)}
              sx={{ color: "#19d27f", cursor: "pointer" }}
            />
          </IconWrapper>
          <IconWrapper>
            <DeleteOutlinedIcon
              className="default-icon"
              sx={{ color: "#d32f2f", cursor: "pointer" }}
            />
            <DeleteIcon
              className="hover-icon"
              onClick={() => handleOpenDeleteModal(website.id!)}
              sx={{ color: "#d32f2f", cursor: "pointer" }}
            />
          </IconWrapper>
        </>
      ),
    },
  ];

  const isLoading = updateAllLoading || updateLoading;
  const checkLoading = loadingDelete || loadingPatchWebsite || loadingOverlay;
  const isError = updateAllError || updateError || errorPatchWebsite;

  return (
    <>
      {scrapingInProgress && (
        <div
          className="d-flex align-items-center justify-content-between gap-3 p-3 mb-4 rounded-3 border"
          style={{
            backgroundColor: "rgba(79, 209, 197, 0.08)",
            borderColor: "#4fd1c5",
          }}
        >
          <div className="flex-grow-1 min-w-0">
            <div className="d-flex align-items-center mb-2">
              <span className="me-2" style={{ fontSize: "1.1rem" }}>
                🔄
              </span>
              <strong className="text-dark">Scraping in Progress</strong>
            </div>
            <div className="small text-secondary mb-2">{scrapingMessage}</div>
            <div
              className="rounded-pill overflow-hidden bg-light"
              style={{ height: "8px" }}
            >
              <div
                className="h-100 rounded-pill"
                style={{
                  width: `${scrapingProgress}%`,
                  backgroundColor: "#4fd1c5",
                  transition: "width 0.5s ease",
                }}
              />
            </div>
            <div className="small text-secondary mt-1">
              {scrapingProgress}% complete
            </div>
          </div>
          <AppButton
            type="button"
            label="View Progress"
            size="sm"
            fullWidth={false}
            onClick={() => onScrapingStart?.()}
          />
        </div>
      )}
      {checkLoading && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(255, 255, 255, 0.7)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
          }}
        >
          <AppLoader />
        </div>
      )}

      {modalMode === "add" || modalMode === "edit" ? (
        <AppModal
          title={modalMode === "edit" ? "Edit Website" : "Add Website"}
          open={openModal}
          handleClose={handleCloseModal}
          action={modalMode === "edit" ? "Update Website" : "Add Website"}
        >
          <AppForm
            inputFields={websiteForm}
            initialValues={
              selectedWebsite || {
                name: "",
                url: "",
                isCustomLogin: false,
                isSourceSite: false,
              }
            }
            schema={websiteSchema}
            handleSubmit={handleSubmitWebsite}
          />
        </AppModal>
      ) : null}

      {modalMode === "delete" && (
        <AppModal
          title="Delete Website"
          open={openModal}
          handleClose={handleCloseModal}
          onSubmit={handleDelete}
          action="Delete"
          color="#dc3545"
        >
          <p>
            Are you sure you want to delete this user? This action cannot be
            undone.
          </p>
        </AppModal>
      )}

      <div className="websites-table-container">
        <div className="mb-3 d-flex justify-content-end gap-2">
          {user?.user_role !== "viewer" && (
            <>
              <AppButton
                label="Export CSV"
                size="sm"
                fullWidth={false}
                disabled={loadingOverlay}
                onClick={exportWebsiteFile}
              />
              <AppButton
                label="Import CSV"
                size="sm"
                fullWidth={false}
                disabled={loadingOverlay}
                onClick={() => setCsvModalOpen(true)}
              />
              <AppButton
                label="Add Website"
                size="sm"
                fullWidth={false}
                onClick={handleOpenAddModal}
              />
            </>
          )}
        </div>

        <AppTable<WebsiteData>
          title="Websites Table"
          columns={extendedColumns}
          renderRowKey={(site) => site.id}
          rows={websites}
          loading={loading || loadingPost}
          error={error}
          onCellClick={async (columnId, value, row) => {
            if (columnId === "errorCount") {
              await handleErrorCountClick(row["id"]);
            }
          }}
          count={meta.totalRecords}
          page={meta.currentPage - 1}
          onPageChange={handleChangePage}
          rowsPerPage={meta.pageSize}
          onRowsPerPageChange={handleChangeRowsPerPage}
          globalToggleChecked={globalEnable}
          onGlobalToggleChange={(checked) => handleAllSwitchButton(checked)}
          handleEnableScraping={handleEnableScraping}
          lastRunDate={
            websites.length > 0
              ? (() => {
                  const latest = websites.reduce<number | null>(
                    (best, site) => {
                      const t = site.scrapedAt
                        ? new Date(site.scrapedAt).getTime()
                        : 0;
                      return isNaN(t)
                        ? best
                        : best == null || t > best
                          ? t
                          : best;
                    },
                    null,
                  );
                  return latest != null
                    ? new Date(latest).toLocaleString(undefined, {
                        dateStyle: "medium",
                        timeStyle: "short",
                      })
                    : null;
                })()
              : null
          }
        />
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            marginTop: "8px",
            marginRight: "8rem",
          }}
        >
          {isLoading && <AppLoader size={24} />}
          {isError && (
            <small style={{ color: "red", marginLeft: "8px" }}>
              {updateError}
            </small>
          )}
        </div>

        <CSVModal
          open={csvModalOpen}
          handleClose={() => setCsvModalOpen(false)}
          handleConfirm={(file) => {
            setCsvModalOpen(false);
            if (file) {
              handleImportCSV(file);
            }
          }}
          title=":globe_with_meridians: CSV Upload Format for Websites"
          formatDetails={
            <>
              <li>
                <b>name</b>: Website name
              </li>
              <li>
                <b>url</b>: Website URL
              </li>
            </>
          }
          sampleFileUrl="/example_websites_data.csv"
          sampleFileName="example_websites_data.csv"
        />
        <AddNewCompetitorCSVModal
          open={visibility}
          handleClose={() => setVisibility(false)}
          handleDownload={async () => {
            if (selectedWebsite)
              await handleCompetitorCSVDownload(selectedWebsite.id);
          }}
          websiteId={selectedWebsite?.id}
          onFileUpload={handleCompetitorCSVUpload}
          text="Download Below File With Pre Filled Data"
          downloadText="Download This File"
          handleConfirm={() => {
            setVisibility(false);
          }}
          title="Add New Competitor"
          formatDetails={
            <>
              <li>ID</li>
              <li>Product Name</li>
              <li>SKU</li>
              <li>Competitor's URL's</li>
            </>
          }
        />
        <ErrorCountModal
          visibility={errorModal}
          //@ts-ignore
          errors={errorLogs}
          onClose={() => setErrorModal(false)}
        />
      </div>
    </>
  );
};

export default WebsitesTable;
